import { faHourglass, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@ui';
import { Category } from 'components/ClubMemberManagement/types';
import { useClubSlug } from 'components/Clubs/hooks';
import { FormInput } from 'components/Form/next';
import FormErrorMessage from 'components/Form/next/FormErrorMessage';
import { Option } from 'components/Form/next/FormSelect';
import { FormInputProps } from 'components/Form/next/props';
import Joi from 'joi';
import moment from 'moment';
import { useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import useSWR from 'swr';

export const guestRulesValidationRules = Joi.array()
    .items(
        Joi.object({
            guest_count: Joi.number().min(1).required().label('Anzahl Gäste'),
            for_category: Joi.number().min(0).allow('', null, 'no-member', 'member').label('Kategorie'),
            for_weekdays: Joi.array().allow('', null).items(Joi.number().min(0).max(6)).label('Wochentage'),
            max_slots: Joi.number().allow(null, '').max(96).label('Maximale Buchungsdauer'),
            price: Joi.number().allow(null, '').max(1000).label('Aufpreis'),
            taxes: Joi.number().allow(null, '').max(100).min(0).label('MwSt'),
            price_per_slot: Joi.boolean().allow(null, '').label('Fixpreis / Slotpreis')
        })
    )
    .unique(
        (a, b) =>
            a.guest_count === b.guest_count && a.for_category === b.for_category && a.for_weekdays === b.for_weekdays
    )
    .messages({ 'array.unique': '***Die Anzahl der Gäste darf nur einmal vorkommen.' })
    .label('Gastregeln');

interface Props extends FormInputProps {
    label?: string;
}

export const GuestRulesFieldArray = ({ name, label }: Props) => {
    const clubSlug = useClubSlug();
    const { control } = useFormContext();
    const { append, fields, remove } = useFieldArray({ name, control });
    const { data: categoryData } = useSWR<{ data: Category[] }>(`club-member/${clubSlug}/category`);

    moment.weekdays;

    const options = useMemo(
        (): Option[] => [
            { key: '', label: 'egal' },
            { key: 'no-member', label: 'kein Mitglied' },
            { key: 'member', label: 'Mitglied, egal welcher Kategorie' },
            ...(categoryData?.data || []).map((cat) => ({
                key: cat.id,
                label: 'in ' + cat.title
            }))
        ],
        [categoryData]
    );

    return (
        <div>
            <Button className="tw-mt-4 tw-w-full" size="sm" onPress={() => append({})}>
                {label || 'Gastregel hinzufügen'}
            </Button>
            {fields.map((field, index) => (
                <div
                    className="tw-my-6 tw-grid tw-grid-cols-[repeat(3,auto)] tw-items-baseline tw-gap-x-4 tw-rounded-md tw-border tw-border-solid tw-border-accents-4 tw-p-6"
                    key={field.id}
                >
                    <FormInput
                        type="number"
                        label="Anzahl Mitspieler"
                        contentLeft="Ab"
                        name={`${name}.${index}.guest_count`}
                    />
                    <FormInput.Select
                        name={`${name}.${index}.for_category`}
                        label="Hauptbucher ist"
                        options={options}
                    />
                    <FormInput.AutoComplete
                        label="für Wochentage"
                        name={`${name}.${index}.for_weekdays`}
                        multiple
                        positionFixed
                        data={moment.weekdays(true).map((day, index) => ({ label: day, value: index }))}
                        dataKey="label"
                        minimizedDataKey="value"
                    />
                    <FormInput
                        type="number"
                        label="Maximale Buchungsdauer in Slots"
                        helperText="Ein Slot entspricht dabei 15 Minuten"
                        placeholder="unbegrenzt"
                        name={`${name}.${index}.max_slots`}
                    />
                    <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                        <FormInput
                            type="number"
                            step={0.01}
                            label="Aufpreis"
                            placeholder="keine Veränderung"
                            name={`${name}.${index}.price`}
                            contentRight="€"
                        />
                        <FormInput
                            type="number"
                            step={0.01}
                            label="MwSt"
                            placeholder="19"
                            name={`${name}.${index}.taxes`}
                            contentRight="%"
                        />
                    </div>
                    <div className="d-flex flex-column align-items-center">
                        <span className="tw-mb-3 tw-text-sm">Fixpreis / Slotpreis</span>
                        <FormInput.Switch
                            name={`${name}.${index}.price_per_slot`}
                            labelOff={null}
                            labelOn={<FontAwesomeIcon icon={faHourglass} />}
                        />
                    </div>
                    <Button
                        icon={<FontAwesomeIcon icon={faTrash} />}
                        onPress={() => remove(index)}
                        color="error"
                        size="xs"
                        css={{ alignSelf: 'end', w: '100%', mt: '$6', gridColumn: '1/4' }}
                    />
                </div>
            ))}
            <FormErrorMessage name={name} />
        </div>
    );
};
